<template>
    <div>
        <kgr-loading v-if="loading" />
        <div v-else>
            <kgr-empty-data
                v-if="empty && !loading"
                :value="$t('Keine Daten vorhanden')"
            />
            <div
                v-if="!empty"
                class="chart"
            >
                <highcharts :options="chartOptionsDetails" />
            </div>
            <div
                v-if="!empty"
                class="chart"
            >
                <highcharts :options="chartOptionsMaster" />
            </div>
        </div>
        <div>
            <kgr-button
                v-if="!loading"
                :disabled="jobRunning"
                @click="createUpdateJob"
            >
                {{ $t('Aktualisieren') }}
            </kgr-button>
            <span v-if="jobRunning">{{ $t('Die Daten werden im Hintergrund berechnet. Die Grafik aktualisiert sich sobald die Daten verfügbar sind.') }}</span>
        </div>
    </div>
</template>

<script>
import ClubStatsRepository from '../../js/repository/ClubStatsRepository';
import EmptyData from '../common/EmptyData';
import Highcharts from 'highcharts';
import KgrButton from '../common/form/button/KgrButton';
import Loading from '../common/Loading';
import More from 'highcharts/highcharts-more';

More(Highcharts);

export default {
    name: 'KgrMembershipCountOverTime',
    components: {
        'kgr-empty-data': EmptyData,
        'kgr-loading': Loading,
        'kgr-button': KgrButton,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        departmentId: {
            type: Number,
            required: false,
            default: null,
        },
        teamId: {
            type: Number,
            required: false,
            default: null,
        },
        name: {
            type: String,
            required: false,
            default: '',
        },
    },
    data() {
        return {
            empty: true,
            loading: true,
            jobRunning: false,
            seriesData: [],
            chartOptionsDetails: {
                chart: {
                    plotBackgroundColor: null,
                    plotShadow: false,
                    maxHeight: 250,
                    height: 250,
                    marginLeft: 50,
                    marginRight: 20,
                },
                yAxis: {
                    title: {
                        text: null,
                    },
                },
                xAxis: {
                    type: 'datetime',
                    dateTimeLabelFormats: {
                        month: '%m.%y',
                        year: '%Y',
                    },
                    gridLineWidth: 1,
                    plotBands: [{
                        color: 'rgba(0, 0, 0, 0.1)',
                    }],
                    title: {
                        text: null,
                    },
                },
                title: {
                    text: this.$t('club.stats.membership.timeline.title'),
                },
                tooltip: {
                    xDateFormat: '%d.%m.%Y',
                    shared: true,
                    pointFormat: `<span class="kgr-text--bold">{point.y}</span> ${this.$t('Mitglieder')} `
                        + `<span style="color:green;"> (+{point.plus})</span>`
                        + `<span style="color:red;"> (-{point.minus})</span>`,
                },
                series: [{
                    name: 'Gesamt',
                    data: [[]],
                    color: '#090345AA',
                    pointIntervalUnit: 'month',
                    pointStart: null,
                }],
                credits: {
                    enabled: false,
                },
                legend: {
                    enabled: false,
                    itemStyle: {
                        fontWeight: 'var(--kgr-font-weight-bold)',
                    },
                },
            },
            chartOptionsMaster: {
                chart: {
                    plotBackgroundColor: null,
                    plotShadow: false,
                    borderWidth: 0,
                    maxHeight: 150,
                    height: 150,
                    marginLeft: 50,
                    marginRight: 20,
                    zoomType: 'x',
                    events: {
                        selection: (event) => {
                            this.selectDetails(event);
                            return false;
                        },
                    },
                },
                accessibility: {
                    enabled: false,
                },
                xAxis: {
                    type: 'datetime',
                    dateTimeLabelFormats: {
                        month: '%m.%Y',
                        year: '%Y',
                    },
                    showLastTickLabel: true,
                    plotBands: [{
                        color: 'rgba(0, 0, 0, 0.1)',
                        from: null,
                        to: null,
                    }],
                    title: {
                        text: this.$t('Zeitstrahl (Monat-Jahr)'),
                    },
                },
                yAxis: {
                    gridLineWidth: 0,
                    title: {
                        text: null,
                    },
                    labels: {
                        enabled: true,
                        useHTML: true,
                        formatter() {
                            return `<span style="color:transparent">${this.value}</span>`;
                        },
                    },
                    min: 0,
                },
                tooltip: {
                    formatter() {
                        return false;
                    },
                },
                title: {
                    text: null,
                },
                series: [{
                    data: [[]],
                    type: 'area',
                    pointIntervalUnit: 'month',
                    pointStart: null,
                }],
                credits: {
                    enabled: false,
                },
                plotOptions: {
                    area: {
                        color: '#00000000',
                        fillColor: {
                            linearGradient: {
                                x1: 0,
                                y1: 0,
                                x2: 0,
                                y2: 1,
                            },
                            stops: [
                                [0, '#090345AA'],
                                [1, '#FFFFFFAA'],
                            ],
                        },
                        lineWidth: 1,
                        states: {
                            hover: {
                                lineWidth: 1,
                            },
                        },
                        threshold: null,
                    },
                },
                legend: {
                    enabled: false,
                    itemStyle: {
                        fontWeight: 'var(--kgr-font-weight-bold)',
                    },
                },
            },
        };
    },
    mounted() {
        this.loadMembershipCountOverTimeStats();
    },
    methods: {
        loadMembershipCountOverTimeStats() {
            ClubStatsRepository.getMembershipCountOverTimeStats(this.clubId, this.departmentId, this.teamId)
                .then((stats) => {
                    this.seriesData = stats.map((entry) => {
                        return {
                            name: (new Date(entry.date)).getTime(),
                            y: entry.membersCount,
                            plus: entry.plus,
                            minus: entry.minus,
                        };
                    });
                    if (this.seriesData.length === 0) {
                        return;
                    }

                    this.chartOptionsMaster.series[0].data = this.seriesData;
                    this.chartOptionsMaster.series[0].pointStart = this.seriesData[0].name;
                    this.chartOptionsDetails.series[0].data = this.seriesData;
                    this.chartOptionsDetails.series[0].pointStart = this.seriesData[0].name;

                    this.chartOptionsMaster.xAxis.plotBands[0].from = this.chartOptionsMaster.series[0].data[0].name;
                    this.chartOptionsMaster.xAxis.plotBands[0].to = this.chartOptionsMaster.series[0].data[this.chartOptionsMaster.series[0].data.length - 1].name;

                    // this.chartOptionsMaster.events.selection = this.selectDetails;
                })
                .finally(() => {
                    this.empty = this.seriesData.length === 0;
                    this.loading = false;
                });
        },
        loadRunningJob() {
            ClubStatsRepository.getMembershipCountOverTimeStatsJob(this.clubId, this.departmentId, this.teamId)
                .then((response) => {
                    this.jobRunning = response.isRunning;
                    if (this.jobRunning === false) {
                        this.loadMembershipCountOverTimeStats();
                    }
                })
                .finally(() => {
                    if (this.jobRunning === true) {
                        setTimeout(() => {
                            this.loadRunningJob();
                        }, 1000);
                    }
                });
        },
        createUpdateJob() {
            ClubStatsRepository.putMembershipCountOverTimeStatsJob(this.clubId, this.departmentId, this.teamId)
                .then(() => {
                    this.jobRunning = true;
                    this.loadRunningJob();
                });
        },
        selectDetails(event) {
            this.chartOptionsDetails.series[0].data = this.seriesData.filter(
                (element) => element.name >= event.xAxis[0].min && element.name <= event.xAxis[0].max,
            );
            this.chartOptionsDetails.series[0].pointStart = this.chartOptionsDetails.series[0].data[0].name;

            this.chartOptionsMaster.xAxis.plotBands[0].from = event.xAxis[0].min;
            this.chartOptionsMaster.xAxis.plotBands[0].to = event.xAxis[0].max;
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss">
    .chart {
    margin: 0 auto;
}
</style>
